import React, { createContext, useContext } from 'react';
import { useKeycloak } from '@react-keycloak/web';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const { keycloak, initialized } = useKeycloak();

  const getRoles = () => {
    if (!keycloak.tokenParsed) return [];

    // Combine realm roles and resource access roles
    const realmRoles = keycloak.tokenParsed.realm_access?.roles || [];
    const resourceRoles = keycloak.tokenParsed.resource_access?.[keycloak.clientId]?.roles || [];

    return [...realmRoles, ...resourceRoles];
  };

  const hasRole = (role) => {
    return getRoles().includes(role);
  };

  const value = {
    isAuthenticated: !!keycloak.authenticated,
    user: keycloak.tokenParsed ? {
      name: keycloak.tokenParsed.name || keycloak.tokenParsed.preferred_username,
      email: keycloak.tokenParsed.email,
      username: keycloak.tokenParsed.preferred_username,
      roles: getRoles()
    } : null,
    token: keycloak.token,
    login: keycloak.login,
    logout: keycloak.logout,
    hasRole,
    isAuditor: hasRole('auditor') || hasRole('admin'),
    isAdmin: hasRole('admin'),
    initialized,
    loading: !initialized
  };

  if (!initialized) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Initializing authentication...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
